/**
 * 业务操作处理管理
 * 思考todo:
 * 1. 每个业务操作是否需要主动销毁(对象以及数据引用)，主要考虑是否会占用较多内存，进而有可能会影响业务性能。
 */
import { HB_EVENT_NAME, BIZ_BST, BIZ_BRP, RESOURCE_TYPE, BIZOP_TYPE } from "src/constant";
import { isWorkbench } from "src/utils";
import getUniqueStr from 'src/utils/get-unique-str';
import getResponseData from "src/utils/get-response-data";
import { IHBReqEvent } from "src/types/type";
import resourceXhr from "./resource-xhr";
import getPageTabTitle from "src/utils/get-page-tab-title";
import getWindowTitle from "src/utils/get-window-title";
import checkFixXhr from "src/utils/check-fix-xhr";
import getErrorEventFromRequest from "src/utils/get-error-event-from-request";

// 当前业务操作，等待关联状态。同一时间，只能有一个业务操作，所有只能有一个等待关联的业务操作
let currentBizOp: BizOp = null;
// 上一个被替换的业务操作（录制结束时需同步捞出其中的事件）
let prevBizOp: BizOp = null;
// 业务操作初始有效关联时间
const BIZ_TIME_SCOPE = 4 * 1000;
// 业务操作最大等待关联的时间
const BIZ_MAX_WAIT_TIME = 60 * 1000;
// 业务ajax异步响应后最小延迟等待关联时间
const BIZ_AJAX_RELX_DELAY_MIN_TIME = 800;
// 业务操作ajax异步最大等待时间
const BIZ_AJAX_WAIT_MAX_TIME = 30 * 1000;

interface BizOpData extends BizOpInfo {
  bt: number; // 业务操作开始时间
}
type BizOpCallback = (hbEventDatas: HBEventData[], isValid: boolean) => void;
interface BizOpNewParams extends BizOpData {
  callback: BizOpCallback,
}
/**
 * 业务操作
 */
enum BIZOP_STATUS {
  WAIT_EVENT,
  CLOSE_WAIT_RESPONSE,
  CLOSED,
}

export class BizOp {
  status: BIZOP_STATUS; // 业务状态
  bType: BIZOP_TYPE; // 业务操作类型
  data: BizOpData; // 业务数据
  isValid: boolean; // 是否是有效的业务操作，目前规则是否关联了异步请求
  events: HBEventData[]; // 关联的事件队列
  timer: any;
  maxTimer: any;
  timeoutTimer: any;
  pageTabTitle: string; // 页签标题
  waitCount: number; // 异步等待数量
  callback: BizOpCallback;

  constructor({
    callback,
    ...dataParams
  }: BizOpNewParams) {
    this.data = {
      ba: '',
      dtxt: '',
      bid: getUniqueStr(dataParams.bt),
      ...dataParams,
      bst: BIZ_BST.INCOMPLETE,
      brp: BIZ_BRP.NO,
    };
    hbLogger.debug(this.data.bid, 'bizop.constructor', Date.now());
    this.events = [];
    this.status = BIZOP_STATUS.WAIT_EVENT;
    this.bType = dataParams.btype as BIZOP_TYPE;
    this.callback = callback;
    this.isValid = false;
    this.waitCount = 0;

    this.pageTabTitle = getPageTabTitle();

    // 非自定义类型业务操作和页面访问类型业务操作，开启等待关联逻辑
    if (!/^[1-9]\d+$/.test(this.bType) && this.bType !== BIZOP_TYPE.PAGEVIEW) {
      // 设置等待超时关闭逻辑
      this.timer = setTimeout(() => {
        this.stopWaitEvent();
      }, BIZ_TIME_SCOPE);
      this.maxTimer = setTimeout(() => {
        this.maxTimer = null;
        this.stopWaitEvent();
      }, BIZ_MAX_WAIT_TIME);
    } else {
      // 自定义类型业务操作或者页面访问类型业务操作，直接标记有效
      this.isValid = true;
    }
  }
  push(mEvent: HBEventData) {
    if (this.status === BIZOP_STATUS.CLOSED) {
      hbLogger.warn('biz closed! push ignored', this.data.ba, this.data.bid, mEvent);
      this.callback([mEvent], false);
      return;
    }
    hbLogger.debug('bizop.push', this.data.bid, mEvent);
    this.events.push(mEvent);
    if (mEvent.en === HB_EVENT_NAME.REQUEST && mEvent.rtype === RESOURCE_TYPE.XHR) {
      this.isValid = true;
      const reqEvent = <IHBReqEvent>mEvent;
      if(reqEvent._wait && this.bType !== BIZOP_TYPE.PAGEVIEW) {
        this.waitCount += 1;
        reqEvent._onWaitBack = () => {
          this.waitCount -= 1;
          // 当前为等待关联状态时
          // 判断当前剩余等待关联时间是否小于n秒时(n=BIZ_AJAX_RELX_DELAY_MIN_TIME)，小于时，调整为n，大于时不调整
          if(this.status === BIZOP_STATUS.WAIT_EVENT) {
            const nowTime = Date.now();
            const residueTime = BIZ_TIME_SCOPE - (nowTime - this.data.bt);
            if(residueTime < BIZ_AJAX_RELX_DELAY_MIN_TIME) {
              clearTimeout(this.timer);
              this.timer = setTimeout(() => {
                this.stopWaitEvent();
              }, BIZ_AJAX_RELX_DELAY_MIN_TIME);
            }
          }

          // 当前为关闭仍在等待异步状态时
          // 判断waitCount为0时，状态调整关闭，开始进行事件数据处理
          // 判断waitCount仍大于0时，无操作，继续保持状态
          if(this.status === BIZOP_STATUS.CLOSE_WAIT_RESPONSE && this.waitCount === 0) {
            this.closeBiz();
          }

          // 当前为关闭状态时
          // 封装完善单个超时回应事件
          if(this.status === BIZOP_STATUS.CLOSED && this.data.brp === BIZ_BRP.YES) {
            this.formatBizData(reqEvent);
          }
        };
      }
    }
    if (mEvent.en === HB_EVENT_NAME.PAGEVIEW) {
      this.data.bte = mEvent.evt + mEvent.tld;
      // 下个事件周期处理
      setTimeout(() => this.stopWaitEvent(), 0);
    }
  }
  // 关闭业务等待事件关联状态
  stopWaitEvent() {
    currentBizOp = null;
    if (this.status !== BIZOP_STATUS.WAIT_EVENT) {return;}
    if (this.maxTimer) {
      clearTimeout(this.maxTimer);
      this.maxTimer = null;
    }

    this.status = BIZOP_STATUS.CLOSE_WAIT_RESPONSE;
    hbLogger.debug(this.data.bid, 'bizop.stopWaitEvent');

    switch (true) {
      // 自定义类型业务操作
      case /^[1-9]\d+$/.test(this.bType):
        break;
      // 有效业务操作，当前存在异步等待，执行超时等待异步逻辑
      case this.isValid && this.waitCount > 0:
        this.timeoutTimer = setTimeout(() => this.closeBiz(), BIZ_AJAX_WAIT_MAX_TIME);
        break;
        // 有效业务操作，当前无异步等待时
      case this.isValid && this.waitCount === 0:
        this.closeBiz();
        break;
      // 无效的业务操作
      case !this.isValid:
        this.closeBiz();
        break;
      default:
        break;
    }
  }
  // 1. 业务操作对象状态修改为CLOSED
  // 2. 页面加载pageview业务时，过滤掉仍在等待状态的无效数据
  // 3. 执行业务数据封装与格式化处理
  closeBiz(endTime?: number) {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.status === BIZOP_STATUS.CLOSED) {return;}
    this.status = BIZOP_STATUS.CLOSED;
    hbLogger.debug(this.data.bid, 'bizop.closeBiz');
    if (endTime) {
      this.data.bte = endTime;
    }
    if(this.isValid) {
      // 自定义业务操作保留50ms延迟，等待_wait标记更新
      if (/^[1-9]\d+$/.test(this.bType)) {
        setTimeout(() => this.formatBizData(), 50);
      } else {
        this.formatBizData();
      }
    } else {
      this.callback(this.events, false);
      this.events = null;
    }
  }

  // 强制关闭，用于录制结束同步捞取所有事件
  forceClose() {
    if (this.status === BIZOP_STATUS.CLOSED) return;
    clearTimeout(this.timer);
    clearTimeout(this.maxTimer);
    clearTimeout(this.timeoutTimer);
    this.timer = this.maxTimer = this.timeoutTimer = null;
    if (currentBizOp === this) { currentBizOp = null; }
    if (prevBizOp === this) { prevBizOp = null; }

    this.events.forEach(evt => {
      if ((evt as IHBReqEvent)._wait) {
        (evt as IHBReqEvent)._wait = undefined;
        (evt as IHBReqEvent).sat = 'waiting';
      }
    });

    this.status = BIZOP_STATUS.CLOSED;
    this.callback(this.events, false);
    this.events = null;
  }
  // 1. 处理格式化业务操作事件组数据，触发callback
  //    1. 仍在等待异步响应的事件处理，标记并修改业务状态
  //    2. 异步事件同事件关联组合
  //    3. 封装完善业务操作相关字段
  // 2. 处理格式化异步超时回应事件数据，触发callback
  //    1. 异步任务是否全部完成
  //    2. 全部完成，业务操作状态设置为完成状态，计算并设置业务操作结束时间
  //    3. 未全部完成，业务操作状态设置为未完成
  //    4. 触发执行回调callback
  formatBizData(timeoutEvent?: IHBReqEvent) {
    hbLogger.debug(this.data.bid, 'bizop.formatBizData');
    const bizData = this.data;
    if(timeoutEvent) {
      // 已经完成的业务操作，遗留的请求事件判定为不相关的
      if(bizData.bst === BIZ_BST.DONE) {
        this.callback([timeoutEvent], false);
        return;
      }

      if (this.waitCount === 0) {
        bizData.bst = BIZ_BST.DONE;
        bizData.bte = timeoutEvent.txre;
      } else {
        bizData.bst = BIZ_BST.INCOMPLETE;
      }
      const err = timeoutEvent._err as any;
      const same = resourceXhr.findSame(timeoutEvent);
      if(same) {
        timeoutEvent.rtiming = same.rtiming;
      }
      Object.assign(timeoutEvent, getResponseData(<IHBReqEvent>timeoutEvent));
      timeoutEvent.rtype = checkFixXhr(timeoutEvent);
      let errEvent: HBEventData = null;
      if(err) {
        errEvent = Object.assign(getErrorEventFromRequest(timeoutEvent), bizData);
      }
      const reqEvent = { ...timeoutEvent, ...bizData } as HBEventData;
      const ret = [reqEvent];
      if (errEvent) {
        ret.push(errEvent);
      }
      this.callback(ret, true);
    } else {
      let hbEvents = this.events;
      const filterEvents = [];
      const reqErrorEvents = [];
      let bEndTime = 0;
      const pageviewEvt = hbEvents.find(evt => evt.en === HB_EVENT_NAME.PAGEVIEW);
      const clickEvt = hbEvents.find(evt => evt.en === HB_EVENT_NAME.CLICK);
      // 请求事件处理
      // 1. 标记未完成的请求状态waitting
      // 2. 已完成的请求关联performance获取的timing类数据
      // 3. 处理rtype请求类型
      // 4. 处理错误类请求，构造一个请求错误事件
      // 5. 记录最晚回来的请求结束时间
      hbEvents.filter(item => item.en === HB_EVENT_NAME.REQUEST).forEach((item) => {
        if (item.rtype === RESOURCE_TYPE.XHR) {
          if((<IHBReqEvent>item)._wait === true) {
            item.sat = 'waiting';
          } else {
            const err = item._err as any;
            const same = resourceXhr.findSame(item);
            if(same) {
              item.rtiming = same.rtiming;
            }
            Object.assign(item, getResponseData(<IHBReqEvent>item));
            item.rtype = checkFixXhr(item);
            if(err) {
              reqErrorEvents.push(getErrorEventFromRequest(item));
            }
          }
          if (item.txre && item.txre > bEndTime) {
            bEndTime = item.txre;
          }
        } else {
          // 静态资源类非xhr请求，关联进来的也需要参与计算最晚结束时间
          if (item.es && item.evt + item.es > bEndTime) {
            bEndTime = item.evt + item.es;
          }
        }
      });
      hbEvents.push(...reqErrorEvents);

      switch (true) {
        case this.bType === BIZOP_TYPE.PAGEVIEW:
          bizData.btype = BIZOP_TYPE.PAGEVIEW;
          bizData.bst = BIZ_BST.DONE;
          bizData.dtxt = pageviewEvt?.servicename || this.pageTabTitle || pageviewEvt?.ptit || '';
          bizData.ba = this.getBa(pageviewEvt);
          // checkFilter();
          break;
        case this.bType === BIZOP_TYPE.CLICK:
          bizData.btype = BIZOP_TYPE.CLICK;
          bizData.bst = this.waitCount === 0 ? BIZ_BST.DONE : BIZ_BST.INCOMPLETE;
          bizData.dtxt = clickEvt.ntxt;
          bizData.ba = this.getBa(null, clickEvt);
          break;
        // 自定义类型业务操作
        case /^[1-9]\d+$/.test(this.bType):
          bizData.bst = BIZ_BST.DONE;
          // checkFilter();
          break;
        default:
          break;
      }

      // 完成状态时，计算业务结束时间
      if(bizData.bst === BIZ_BST.DONE && !bizData.bte) {
        bizData.bte = bEndTime;
      }
      // 过滤掉事件发生时间或者请求结束时间在完成之后的事件
      hbEvents = hbEvents.filter(evt => {
        const eEnd = evt.txre ? evt.txre : (evt.es ? evt.evt + evt.es : evt.evt);
        const isExclude = eEnd > bizData.bte;
        if (isExclude) {
          filterEvents.push(evt);
        }
        return !isExclude && evt.sat !== 'waiting';
      });
      // 增加按事件时间排序 2024-03-20
      const retHbEvents = hbEvents.sort((a, b) => a.evt - b.evt).map((item, i) => {
        return {
          bno: i,
          ...item,
          ...bizData,
        };
      }) as HBEventData[];

      if (retHbEvents.length > 0 && retHbEvents[0].en === HB_EVENT_NAME.CLICK) {
        const clickEvt = retHbEvents[0];
        const finded = retHbEvents.find(item => item.servicecode && item.servicecode !== clickEvt.servicecode);
        if (finded) {
          clickEvt.tsc = finded?.servicecode;
        }
      }
      if (retHbEvents.length > 0) {
        hbLogger.debug('formatBizData onClosed', this.data.bid, retHbEvents);
        this.callback(retHbEvents, true);
      }
      // 被排除掉认为无关联的事件
      if (filterEvents.length > 0) {
        this.callback(filterEvents, false);
      }
      this.events = null;
      this.data.brp = BIZ_BRP.YES; // 后面再上报的均属于重复再次上报的
    }
  }
  // 业务操作名称生成规则
  // 工作台框架层触发：modal? + btnname;
  // 其他默认兜底情况：(servicename || pageTabTitle || pageTitle) + parentTitles? + modal? + btnname?;
  // 存在工作台兼容
  getBa(pageview: HBEventData, clickEvent?: HBEventData): string {
    const pageTitle = getWindowTitle(true);
    const pageTabTitle = this.pageTabTitle;
    const servicename = this.events.find(item => !!item.servicename)?.servicename;
    let baStr = '';
    if (pageview) {
      baStr = pageview.servicename || pageTabTitle || pageTitle;
    }
    if (clickEvent) {
      const titles: string[] = [];
      // 拼接pageTabTitle
      // 过滤工作台框架层大部分事件对应的业务操作不拼接pageTabTitle的场景
      const workbenchFilter = () => !/#home_header|#helpcenter-wrapper|.workbench-side-menu|#rootChat|#ykj-frame|.close_tabs.diwork-dropdown/.test(clickEvent._fullXpath as string); // 工作台框架层排除规则
      const workbenchInclude = () => /#home_header.*wui-tooltip-content/.test(clickEvent._fullXpath as string); // 页签右击浮层部分添加pageTabTitle，例如刷新操作
      if(isWorkbench()) {
        if(workbenchFilter() || workbenchInclude()) {
          titles.push(clickEvent.servicename || pageTabTitle);
        }
      } else {
        titles.push(servicename || pageTitle);
      }
      // 父级节点titles
      if(clickEvent._parentNodeTitles instanceof Array) {
        titles.push(...clickEvent._parentNodeTitles);
      }
      // 事件节点title
      if (clickEvent.ntxt) {
        titles.push(clickEvent.ntxt);
      }
      if (titles.length === 2 && titles[0] === titles[1]) {
        titles.pop();
      }
      const cBaStr = titles.join('#');
      if (baStr && !cBaStr.includes(baStr)) {
        return clickEvent.evt > pageview.evt ? `${baStr}+${cBaStr}` : `${cBaStr}+${baStr}`;
      }
      return cBaStr;
    }
    return baStr;
  }
}

// 获取当前业务操作对象，考虑父容器开启的业务操作
export function getCurrentBizOp() {
  if (currentBizOp?.status === BIZOP_STATUS.WAIT_EVENT) {
    return currentBizOp;
  }
  currentBizOp = null;
  return null;
}

// 获取一个新生成的业务操作对象
export function getNewBizOp(params: BizOpNewParams) {
    if (currentBizOp) {
      prevBizOp = currentBizOp;
      currentBizOp.stopWaitEvent();
    } else {
      prevBizOp = null;
    }
    currentBizOp = new BizOp(params);

    return currentBizOp;
}

// 获取被替换的上一个业务操作（可能处于CLOSE_WAIT_RESPONSE状态）
export function getPrevBizOp() {
  if (prevBizOp && prevBizOp.status !== BIZOP_STATUS.CLOSED) {
    return prevBizOp;
  }
  return null;
}
